'use client'

import { useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

export function useUrlStateManager() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [storedParams, setStoredParams] = useState<string | null>(null)

  /**
   * Helper to parse and filter parameters.
   */
  const parseParams = (paramsString: string | null) => {
    const params = new URLSearchParams(paramsString || '')
    return params
  }

  /**
   * Helper to merge parameters with overrides.
   */
  const mergeParams = (
    baseParams: URLSearchParams,
    overrides: Record<string, string | null>,
  ) => {
    const mergedParams = new URLSearchParams(baseParams)

    Object.entries(overrides).forEach(([key, value]) => {
      if (value === null) {
        mergedParams.delete(key)
      } else {
        mergedParams.set(key, value)
      }
    })

    return mergedParams
  }

  /**
   * Temporarily clears the current URL parameters and stores them for later restoration.
   */
  const storeUrlParams = ({
    excludeKeys = [],
  }: { excludeKeys?: string[] } = {}) => {
    const params = parseParams(searchParams?.toString())

    // Remove specified keys
    excludeKeys.forEach((key) => {
      params.delete(key)
    })

    setStoredParams(params.toString())
  }

  /**
   * Restores the previously stored URL parameters.
   */
  const restoreUrlParams = ({
    overrides = {},
  }: { overrides?: Record<string, string | null> } = {}) => {
    const baseParams = parseParams(storedParams)
    const finalParams = mergeParams(baseParams, overrides)
    router.replace(`${pathname}?${finalParams.toString()}`)
  }

  return { storeUrlParams, restoreUrlParams }
}
